import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useIsAndroid, useIsIOS } from '@/components/organisms/OfferListingHero/OfferListingHero.hooks';
import { cn } from '@/helpers/cn';
import { focusFirstFocusableIn } from '@/helpers/focusUtils';
import { scrollTo } from '@/helpers/scrollTo';
import { useHasHeader } from '@/hooks/useHasHeader';
import clsx from 'clsx';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useScrollToActiveNavButton } from '../OfferAlphabeticDirectory/OfferAlphabeticDirectory.hooks';
const alphabet = [
    '#',
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
];
const getItemsForLetter = (items, sortProp, letter) => items.filter((item) => {
    const prop = item[sortProp];
    if (letter === '#' && !/^[A-Za-z]/.test(prop)) {
        return true;
    }
    return prop.toLowerCase().startsWith(letter);
});
const AlphabeticDirectory = ({ cardSpacing, items, renderItem, sortProp, }) => {
    const sectionRefs = useRef({});
    const contentRefs = useRef({});
    const containerRef = useRef(null);
    const hasHeader = useHasHeader();
    const initialActiveSection = alphabet.find((letter) => getItemsForLetter(items, sortProp, letter).length > 0) || alphabet[0];
    const [activeSection, setActiveSection] = useState(initialActiveSection);
    const { navButtonRefs, navContainerRef } = useScrollToActiveNavButton(alphabet, activeSection);
    const isIOS = useIsIOS();
    const isAndroid = useIsAndroid();
    const scrollToSectionAndFocus = (sectionKey) => {
        const section = sectionRefs.current[sectionKey];
        scrollTo(section, { accountForHeader: true }, () => {
            if (!isIOS && !isAndroid) {
                focusFirstFocusableIn(contentRefs.current[sectionKey]);
            }
        });
    };
    useEffect(() => {
        const calculateAndSetActiveSection = () => {
            const targetHeight = parseInt(window.getComputedStyle(document.body).getPropertyValue('--header-offset'), 10);
            const inUseSectionRefs = Object.entries(sectionRefs.current).filter(([_, ref]) => !!ref);
            const activeSection = inUseSectionRefs.reverse().find(([_, ref]) => {
                const sectionTop = ref?.getBoundingClientRect().top || 0;
                return sectionTop <= targetHeight;
            });
            const activeSectionKey = activeSection ? activeSection[0] : initialActiveSection;
            setActiveSection(activeSectionKey);
        };
        const controller = new AbortController();
        calculateAndSetActiveSection();
        const resizeObserver = new ResizeObserver(() => calculateAndSetActiveSection());
        window.addEventListener('scroll', calculateAndSetActiveSection, { signal: controller.signal, passive: true });
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            controller.abort();
            resizeObserver.disconnect();
        };
    }, [initialActiveSection]);
    useEffect(() => {
        if (window.location.hash.match(/^[#][a-z]$/)) {
            const letter = window.location.hash.replace('#', '');
            scrollToSectionAndFocus(letter);
        }
    }, []);
    useEffect(() => {
        if (activeSection !== '#') {
            history.replaceState(null, '', `#${activeSection}`);
        }
    }, [activeSection]);
    return (_jsxs("div", { className: "flex", children: [_jsx("div", { className: "order-2 inline w-site-padding shrink-0 py-0.5 lg:py-1", children: _jsxs("nav", { ref: navContainerRef, "aria-label": "Alphabetical navigation", className: cn('sticky top-2.5 ml-auto flex max-h-[calc(100vh-8rem)] w-min flex-col overflow-y-auto md:top-4 landscape:top-1 landscape:max-h-[calc(100vh-2rem)]', hasHeader && 'md:landscape:top-[calc(var(--header-height,0px)+1rem)]'), "data-testid": "alphabetic-directory-navigation", children: [_jsx("p", { className: "sr-only", children: "Jump to section" }), alphabet.map((letter) => (_jsx("button", { ref: navButtonRefs[letter], className: clsx('focus-inset flex aspect-square w-1.5 cursor-pointer items-center justify-center text-center type-button-sm disabled:cursor-default disabled:text-text-placeholder', activeSection === letter
                                ? 'bg-text-primary text-text-contrast !outline-text-hover-contrast'
                                : 'bg-text-contrast text-text-primary'), disabled: getItemsForLetter(items, sortProp, letter).length === 0, role: "link", onClick: () => scrollToSectionAndFocus(letter), onKeyDown: (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    scrollToSectionAndFocus(letter);
                                }
                            }, "aria-label": `Jump to ${letter}`, "aria-current": activeSection === letter, "data-testid": `alphabetic-jump-to-${letter}`, children: letter }, letter)))] }) }), _jsx("div", { ref: containerRef, className: "grid-base relative order-1 grow self-start pr-1 xs:pr-[calc(var(--site-padding)*1.5)] md:pr-0", children: alphabet.map((letter) => {
                    const itemsForLetter = getItemsForLetter(items, sortProp, letter);
                    if (!itemsForLetter.length) {
                        return null;
                    }
                    return (_jsxs(Fragment, { children: [_jsx("h3", { ref: (ref) => (sectionRefs.current[letter] = ref), className: clsx('border-b border-border-primary pt-0.75 type-body-large-demi', cardSpacing === 'FoodAndBeverage' && 'mb-0.5', cardSpacing === 'BrandDetail' && 'mb-0.5', cardSpacing === 'BrandList' && 'my-0.5 first:mt-0 md:mb-1.5'), "data-testid": `alphabetic-listing-${letter}`, children: letter.toUpperCase() }), _jsx("div", { ref: (ref) => (contentRefs.current[letter] = ref), className: clsx(cardSpacing === 'FoodAndBeverage' && 'gap-y-0.5', cardSpacing === 'BrandDetail' && 'gap-y-0.5', cardSpacing === 'BrandList' && 'gap-y-0.5 md:gap-y-1.5', 'grid-base md:grid-cols-3'), children: itemsForLetter.map((item, index) => (_jsx("div", { className: "flex w-full grow", children: renderItem(item, index) }, `${item.name}-${index}`))) })] }, letter));
                }) })] }));
};
export default AlphabeticDirectory;
